import { triggerAction } from "../../constants"
import { backendAPI, transactionAPI } from "../../api"
import { patchMeeting } from "../../actions/meetings/createMeeting"
import {
  db,
  decryptObject,
  deleteCacheByKey,
  getCacheByKey,
} from "../../../view/shared/utilities/cache"
import { safeApiCall } from "../../api/offline"
import {
  EXPENSE_TRANSACTION,
  hydrateOfflineBalance,
  INCOME_TRANSACTION,
} from "../../reducers/transactions/actions"
import { meetingTransactionsLoaded } from "../getTransactions"
import { findMemberName } from "../helpers"

export const updateTransactionStatus = (requestId, status) => ({
  type: "UPDATE_TRANSACTION_STATUS",
  payload: { requestId, status },
})

const EIGHT_HOURS_IN_MILLISECONDS = 2.88e7

export const fetchActiveMeetingTransactions =
  (meetingId, showLoader = true) =>
    async (dispatch, getState) => {
      // `showLoader` is a new parameter to control UI loaders
      const { meeting, user, loadedvslaMembers } = getState()
      const localGroup = await getCacheByKey("groups", "userId", user?.id)
      let id = null
      if (meetingId) {
        id = meetingId
      } else if (meeting && meeting?.ongoing) {
        id = meeting?.ongoing.id
      } else {
        return Promise.reject()
      }

      let transactions = []
      try {
        const url = `/transactions/meetings/${id}?group_id=${meeting?.ongoing?.group_id}`
        const res = await safeApiCall(
          {
            cacheEnabled: true,
            url: url,
          },
          async () => backendAPI.get(url),
        )
        // Use the API response as the base, even if it's an empty array
        transactions =
          res?.data?.sort((a, b) => new Date(b.date) - new Date(a.date)) || []
      } catch (error) {
        // If API call fails (e.g., network error), fall back to cached data
        transactions = meeting?.ongoing?.transactions || []
        console.log(
          "Failed to fetch online transactions, falling back to cached data:",
          error,
        )
      }

      // Always reconcile with offline requests, regardless of online response
      const offlineRequests = await db.offlineRequests.toArray()
      const localMembers = offlineRequests.filter(
        ({ userId, entityType }) => userId === user?.id && entityType === "members",
      )
      const ongoingMeetingTX = offlineRequests.filter(
        ({ userId, entityType }) =>
          userId === user?.id &&
          (INCOME_TRANSACTION.includes(entityType) ||
            EXPENSE_TRANSACTION.includes(entityType)),
      )
      if (ongoingMeetingTX.length) {
        for (const tx of ongoingMeetingTX) {
          try {
            const decryptedString = decryptObject(tx.encryptedBody, tx.salt)
            const decryptedBody = JSON.parse(decryptedString)
            const txData = decryptedBody.body?.data
            if (txData && Number(txData.meeting_id) === Number(id)) {
              const isDuplicate = transactions.some(
                (t) => t.requestId === txData.request_id || t.requestId === txData.requestId,
              )
              console.log("Checking for duplicate transaction:", isDuplicate, txData, transactions)
              if (!isDuplicate) {
                const memberPubkey = loadedvslaMembers.find(
                  (m) => m.id === txData?.member_id ||
                    decryptedBody?.needBodyUpdate?.member_id === m.pubkey,
                )?.pubkey

                transactions.unshift({
                  ...txData,
                  transactionPartyName: findMemberName(
                    memberPubkey,
                    loadedvslaMembers,
                    localMembers,
                    localGroup?.secretary,
                  ),
                  isOffline: true,
                  status: "pending",
                })
              }
            }
          } catch (e) {
            console.error(
              "Failed to decrypt and process offline transaction for UI hydration:",
              e,
            )
          }
        }
      }

      // Sort the final combined list of transactions
      transactions.sort((a, b) => new Date(b.date) - new Date(a.date))

      // Existing meeting timer logic remains below
      if (meeting?.onging) {
        const timePassed = new Date(meeting?.onging?.created_at)
        const currentTime = new Date()
        const timeDiff = currentTime.getTime() - timePassed.getTime()
        const tRef = localStorage.getItem("_tRef")

        const timerId = setTimeout(
          function () {
            // auto close the meeting
            const temp = meeting?.onging
            temp.status = "completed"
            const data = {
              date: new Date().toISOString(),
              status: "completed",
              group_id: temp.group_id,
              msg: "The meeting ended by the system",
            }
            dispatch(patchMeeting(data)).then(() => {
              localStorage.removeItem("_tRef")
            })
            // if 8 hours passed without adding a transaction close the meeting immediatly
            // otherwise close it after the time window completes 8 hours.
          },
          timeDiff > 0 && timeDiff >= EIGHT_HOURS_IN_MILLISECONDS // if the meeting been stale for 8 or more hours
            ? 0 // immediately end the meeting
            : EIGHT_HOURS_IN_MILLISECONDS - timeDiff, // remaining time for the meeting to complete 8 hours
        )
        if (tRef) {
          // clear previous function call schedule
          clearTimeout(tRef)
        }
        // store new function ref
        localStorage.setItem("_tRef", timerId)
      }

      dispatch(triggerAction("SET_ACTIVE_MEETING_TRANSACTIONS", transactions))
      dispatch(hydrateOfflineBalance())
      return Promise.resolve(transactions)
    }

export const fetchPreviousMeetingsData = () => async (dispatch, getState) => {
  try {
    const { group } = getState()
    if (!group?.id) {
      console.error("Group not found or group ID is missing.")
      return Promise.reject(new Error("Group not found or group ID is missing."))
    }

    const url = `/groups-hedera/${group?.id}/meetings`
    const { data } = await safeApiCall(
      {
        cacheEnabled: true,
        url: url,
      },
      async () => backendAPI.get(url),
    )
    dispatch(
      triggerAction(
        "SET_PREVIOUS_MEETING_DATA",
        data.sort((a, b) => new Date(b.date) - new Date(a.date)),
      ),
    )

    dispatch(fetchCycles())
    return Promise.resolve()
  } catch (error) {
    console.log("Error: ", error)
    return Promise.reject()
  }
}

export const fetchCycles = () => {
  return async (dispatch, getState) => {
    try {
      const { group } = getState()
      if (!group?.id) {
        console.error("Group not found or group ID is missing.")
        return Promise.reject(new Error("Group not found or group ID is missing."))
      }
      const { data } = await safeApiCall(
        {
          cacheEnabled: true,
          url: `/groups-hedera/${group?.id}/cycles`,
        },
        async () => backendAPI.get(`/groups-hedera/${group?.id}/cycles`),
      )
      dispatch(triggerAction("SET_CYCLES", data))
      return Promise.resolve(data)
    } catch (error) {
      console.error("Error fetching cycles:", error)
      return Promise.reject(error)
    }
  }
}

export const selectPreviousMeeting = (item) => (dispatch) => {
  dispatch(triggerAction("SET_SELECTED_PREVIOUS_MEETING_DATA", item))
}

export const selectCycle = (item) => (dispatch) => {
  dispatch(triggerAction("SET_SELECTED_CYCLE", item))
}

export const uploadOfflineMeetings = (meeting) => async (dispatch, getState) => {
  const { vsla, user, uploadStatus } = getState()
  const cacheKey = meeting.cacheId
  delete meeting.cacheId
  try {
    if (meeting.status !== "completed" || uploadStatus.isUpLoading) {
      return
    }
    dispatch(triggerAction("TOGGLE_IS_UPLOADING", true))
    const requestObject = {
      pushEvent: "requests",
      receiveEvent: "response",
      emit: true,
      metadata: {
        userId: vsla?.id,
        cacheKey,
        callBackParams: [],
        type: "offlineMeeting",
      },
      payload: {
        requestId: "vsla.offline_meeting.new",
        url: `${window.REACT_APP_PWA_APP_DOMAIN}/meetings/transactions`,
        headers: {
          Authorization: "Bearer " + user.token || localStorage.getItem("token"),
        },
        data: { ...meeting, userId: user.id },
        userId: vsla?.id,
        method: "POST",
      },
    }
    dispatch(requestObject)
  } catch (error) {
    console.log("error uploading ", error)
    dispatch(triggerAction("TOGGLE_FAILED_TO_UPLOAD", true))
  }
  setTimeout(() => {
    const cacheId = cacheKey
    deleteCacheByKey("offlineMeeting", "cacheId", cacheId)
  }, 10000)
}
